import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/lib/site";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function NavBar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-200 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-hair"
          : "bg-white/60 backdrop-blur-md border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src={COMPANY.logo}
            alt="Infokarta logo"
            className="w-10 h-10 rounded-full object-cover ring-1 ring-hair"
          />
          <div className="leading-tight">
            <div className="font-brand text-[17px] font-bold tracking-tight text-ink uppercase">{COMPANY.short}</div>
            <div className="overline text-[10px] text-ink-soft -mt-0.5">d.o.o. · Split</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-2">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `px-4 py-2 text-base font-medium transition-colors ${
                  isActive ? "text-ink" : "text-ink-soft hover:text-ink"
                }`
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to="/prijava"
            className="hidden sm:inline-flex items-center gap-2 bg-ink text-white px-5 py-2.5 text-base font-medium rounded-sm hover:bg-[#112240] transition-colors"
            data-testid="nav-cta-prijava"
          >
            {t("nav.cta")}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
          <button
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen((o) => !o)}
            aria-label={t("nav.openMenu")}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-hair bg-white" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-hair ${
                    isActive ? "text-ink" : "text-ink-soft"
                  }`
                }
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
            <Link
              to="/prijava"
              onClick={() => setOpen(false)}
              className="mt-3 bg-ink text-white px-5 py-3 text-base font-medium rounded-sm text-center"
            >
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
